import React from "react";
import { Project } from "../types";

interface ProjectCardProps {
  project: Project;
  view: "grid" | "list";
}

// Declare the plausible function type
declare global {
  interface Window {
    plausible?: (
      eventName: string,
      options?: {
        callback?: () => void;
        props?: Record<string, string | number>;
        revenue?: Record<string, number>;
        interactive?: boolean;
      }
    ) => void;
  }
}

export const ProjectCard: React.FC<ProjectCardProps> = ({ project, view }) => {
  const badges: JSX.Element[] = [];

  if (project.hlNative === "Yes") {
    badges.push(
      <span key="native" className="badge badge-native">
        Native
      </span>
    );
  }

  if (project.points === "Yes") {
    badges.push(
      <span key="points" className="badge badge-points">
        Points
      </span>
    );
  } else if (project.points === "Not Live") {
    badges.push(
      <span key="not-live" className="badge badge-not-live">
        Not Live
      </span>
    );
  }

  const tags = project.category.split(",").map((tag) => (
    <span key={tag.trim()} className="tag">
      {tag.trim()}
    </span>
  ));

  // Function to handle tracking with custom properties
  const trackVisit = (url: string, linkType: "website" | "twitter") => {
    if (window.plausible) {
      window.plausible("VisitWebsite", {
        props: {
          url: url,
          projectName: project.name,
          linkType: linkType,
          category: project.category,
        },
      });
    }
  };

  const actions: JSX.Element[] = [];
  if (project.website && project.website !== "N/A") {
    actions.push(
      <a
        key="website"
        href={project.website}
        target="_blank"
        rel="noopener noreferrer"
        className="action-btn primary"
        onClick={(e) => {
          e.stopPropagation();
          trackVisit(project.website!, "website");
        }}
      >
        Visit
      </a>
    );
  }
  if (
    project.twitter &&
    project.twitter !== "N/A" &&
    project.twitter !== "Nil"
  ) {
    actions.push(
      <a
        key="twitter"
        href={project.twitter}
        target="_blank"
        rel="noopener noreferrer"
        className="action-btn"
        onClick={(e) => {
          e.stopPropagation();
          trackVisit(project.twitter!, "twitter");
        }}
      >
        Twitter
      </a>
    );
  }

  // Determine the primary link for the card
  const primaryLink =
    project.website && project.website !== "N/A"
      ? project.website
      : project.twitter &&
        project.twitter !== "N/A" &&
        project.twitter !== "Nil"
      ? project.twitter
      : null;

  const cardContent = (
    <>
      {view === "list" ? (
        <>
          <div className="project-info">
            <div className="project-header">
              <h3 className="project-name">{project.name}</h3>
              <div className="project-badges">{badges}</div>
            </div>
            <p className="project-description">{project.description}</p>
            <div className="project-tags">{tags}</div>
          </div>
          <div className="project-actions">{actions}</div>
        </>
      ) : (
        <>
          <div className="project-header">
            <h3 className="project-name">{project.name}</h3>
            <div className="project-badges">{badges}</div>
          </div>
          <p className="project-description">{project.description}</p>
          <div className="project-tags">{tags}</div>
          <div className="project-actions">{actions}</div>
        </>
      )}
    </>
  );

  if (primaryLink) {
    return (
      <a
        href={primaryLink}
        target="_blank"
        rel="noopener noreferrer"
        className="project-card"
        style={{ textDecoration: "none", color: "inherit", cursor: "pointer" }}
        onClick={() => {
          const linkType =
            primaryLink === project.website ? "website" : "twitter";
          trackVisit(primaryLink, linkType);
        }}
      >
        {cardContent}
      </a>
    );
  }

  return <div className="project-card">{cardContent}</div>;
};
